import React, { useState } from "react";
import { Button, Input, StyledModal } from "@bikdotai/bik-component-library";
import UploadIcon from "../../icons/UploadIcon";
import VideoCamcorderIcon from "../../icons/VideoCamcorderIcon";
import { FirebaseService } from "../../services/firebase/FirebaseService";
import { voidFunction } from "../../app/messageBlock/Constants";
import { getDownloadURL } from "firebase/storage";
import { useAppSelector } from "../../state/store";

interface VideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVideoUpload: (videoUrl: string) => void;
}

const VideoUploadModal: React.FC<VideoUploadModalProps> = ({
  isOpen,
  onClose,
  onVideoUpload,
}) => {
  const [videoUrl, setVideoUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const uploadIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const homeState = useAppSelector((state) => state.homeState);
  const firebaseService = new FirebaseService(voidFunction, voidFunction);

  const handleSubmit = () => {
    if (videoUrl.trim()) {
      onVideoUpload(videoUrl.trim());
      setVideoUrl("");
      onClose();
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const getFileName = (fileName: string) => {
    const extension = fileName.split(".").pop();
    const date = new Date();
    return date.getTime().toString() + "." + extension;
  };

  const getMediaPath = (path: string) => {
    return `chatbot/${homeState.storeId}/${path}`;
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const dm = 2;
    const sizes = ["Bytes", "KB", "MB"];
    const power = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, power)).toFixed(dm))} ${sizes[power]}`;
  };

  const getMetaData = (file: File, url: string, fileName: string) => {
    return {
      mediaUrl: url,
      mediaSize: formatFileSize(file.size) || "0 Bytes",
      mediaName: fileName,
    };
  };

  const uploadVideoToFirebase = async (file: File) => {
    const fileName = getFileName(file.name);
    const uploadPath = getMediaPath(fileName);

    console.log("Starting video upload to Firebase...");
    console.log("File name:", fileName);
    console.log("Upload path:", uploadPath);
    console.log("File size:", file.size);

    const uploadRef = firebaseService.getUploadRef(file, uploadPath);

    return new Promise<string>((resolve, reject) => {
      uploadRef.on(
        "state_changed",
        (snapshot) => {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          console.log(`Upload progress: ${percent}%`);
          setUploadProgress(percent);
        },
        (error) => {
          console.error("Error uploading video:", error);
          reject(error);
        },
        () => {
          console.log("Upload completed, getting download URL...");
          getDownloadURL(uploadRef.snapshot.ref)
            .then((url) => {
              console.log("Download URL obtained:", url);
              resolve(url);
            })
            .catch((error) => {
              console.error("Error getting download URL:", error);
              reject(error);
            });
        }
      );
    });
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log("File selected:", file.name, file.type, file.size);

      // Check if file is a video
      if (!file.type.startsWith("video/")) {
        alert("Please select a video file (MP4, MOV, AVI, etc.)");
        return;
      }

      // Check file size (50MB limit for videos)
      if (file.size > 50 * 1024 * 1024) {
        alert("File size must be less than 50MB");
        return;
      }

      // Start upload process
      setIsUploading(true);
      setUploadProgress(0);

      try {
        // Upload to Firebase and get the download URL
        const downloadUrl = await uploadVideoToFirebase(file);

        console.log(
          "Upload successful, calling onVideoUpload with:",
          downloadUrl
        );

        // Call the onVideoUpload callback with the Firebase URL
        onVideoUpload(downloadUrl);
        onClose();

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (error) {
        console.error("Error uploading video:", error);
        alert("Error uploading video. Please try again.");
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  };

  const handleCancel = () => {
    // Clear the upload interval if it exists
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }
    setIsUploading(false);
    setUploadProgress(0);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (isUploading) {
    return (
      <StyledModal
        open={isOpen}
        headingTitle=""
        onClose={onClose}
        width="480px"
      >
        <div style={{ padding: "40px 24px", textAlign: "center" }}>
          {/* Icon container with gray background */}
          <div
            style={{
              backgroundColor: "#f5f5f5",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px auto",
            }}
          >
            <VideoCamcorderIcon width="40" height="40" fill="#9e9e9e" />
          </div>

          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            Video upload in progress...
          </h3>
          <p
            style={{ margin: "0 0 32px 0", color: "#6b7280", fontSize: "14px" }}
          >
            Your video is getting loaded. This may take a couple of minutes.
          </p>

          {/* Progress bar */}
          <div
            style={{
              width: "100%",
              height: "8px",
              backgroundColor: "#e5e7eb",
              borderRadius: "4px",
              marginBottom: "32px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${uploadProgress}%`,
                height: "100%",
                backgroundColor: "#10b981",
                transition: "width 0.2s ease",
              }}
            />
          </div>

          <button
            onClick={handleCancel}
            style={{
              backgroundColor: "transparent",
              border: "1px solid #d1d5db",
              color: "#6b7280",
              borderRadius: "8px",
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            Cancel
          </button>
        </div>
      </StyledModal>
    );
  }

  return (
    <StyledModal
      open={isOpen}
      headingTitle="Update template media"
      onClose={onClose}
      width="480px"
    >
      <div style={{ padding: "24px" }}>
        {/* Dashed border container */}
        <div
          style={{
            border: "2px dashed #d1d5db",
            borderRadius: "12px",
            padding: "32px 24px",
            textAlign: "center",
            marginBottom: "24px",
          }}
        >
          {/* Icon container with gray background */}
          <div
            style={{
              backgroundColor: "#f5f5f5",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px auto",
            }}
          >
            <VideoCamcorderIcon width="40" height="40" fill="#9e9e9e" />
          </div>

          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            Add video
          </h3>
          <p
            style={{ margin: "0 0 24px 0", color: "#6b7280", fontSize: "14px" }}
          >
            MP4/GIF format
          </p>

          <div style={{ marginBottom: "16px", textAlign: "left" }}>
            <p
              style={{
                margin: "0 0 8px 0",
                fontSize: "14px",
                color: "#374151",
                fontWeight: "500",
              }}
            >
              Video link
            </p>
            <Input
              value={videoUrl}
              onChangeText={setVideoUrl}
              placeholder="Enter URL of the video"
            />
            {videoUrl.trim() && (
              <div style={{ marginTop: "8px" }}>
                <Button
                  buttonText="Add Video"
                  onClick={handleSubmit}
                  size="medium"
                />
              </div>
            )}
          </div>

          <p style={{ margin: "16px 0", color: "#6b7280", fontSize: "14px" }}>
            Or upload video from device
          </p>

          {/* Upload button matching the main modal style */}
          <div
            onClick={handleFileUpload}
            style={{
              border: "1px solid #731DCF",
              borderRadius: "8px",
              padding: "12px 16px",
              cursor: "pointer",
              backgroundColor: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "border-color 0.2s ease",
              width: "100%",
            }}
          >
            <UploadIcon width="20" height="20" fill="#731DCF" />
            <span
              style={{ fontSize: "14px", fontWeight: "500", color: "#731DCF" }}
            >
              Upload
            </span>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>
      </div>
    </StyledModal>
  );
};

export default VideoUploadModal;
